import {
  resolveImagerySource,
  lonToTileX,
  latToTileY,
  type ImagerySource,
} from '../../map/services/imagerySources'
import {
  drawAttribution,
  drawAttributionAt,
  drawBadgeAt,
  drawBoundary,
  drawLeaderLine,
  drawLocatorInset,
  drawMarker,
  drawMutedDot,
  drawScaleBar,
  drawSheetOutline,
  drawTiles,
  drawTreeDot,
} from './siteMap/siteMapCanvas'
import { planSiteMap } from './siteMap/mapBookPlanner'
import {
  BADGE_HEIGHT_PT,
  BADGE_PAD_PT,
  BADGE_TEXT_PT,
  PX_PER_PT,
  SINGLE_MAP_HEIGHT as CANVAS_HEIGHT,
  SINGLE_MAP_WIDTH as CANVAS_WIDTH,
  TILE_SIZE,
  fitZoom,
  scaleBarBoxPt,
  singleMapPtPerPx,
  worldPx,
} from './siteMap/siteMapGeometry'
import { createTileSession, planTileZooms, type TileSession } from './siteMap/tileBudget'
import type {
  Density,
  LatLng,
  PointPt,
  OverviewFrame,
  SheetPlan,
  SiteMapLayout,
  SiteMapRender,
  SiteTree,
} from './siteMap/types'

/**
 * Generates a static site-overview map image with labelled tree markers by
 * compositing raster imagery tiles onto a canvas — no map library and no
 * paid static-map API involved.
 *
 * Feature 004: the imagery source is region-resolved (deepest licensed
 * regional source, else the authenticated Esri fallback), captured at up to
 * the source's native depth and at a fractional zoom that fills the frame.
 * Each missing tile falls back to a scaled parent-tile quadrant, then to the
 * underlay source (both the regional services and the authenticated Esri
 * endpoint 404s where imagery runs out instead of serving placeholders).
 * The credit strip names every source that supplied a tile.
 *
 * Used for the auto-generated "Site Overview Map" page of the PDF report and
 * for the on-screen report preview. Generation is best-effort: it needs
 * network access for tiles, and callers must treat a null result (or a thrown
 * error) as "no map available" rather than a failure.
 */

export interface SiteMapMarker {
  latitude: number
  longitude: number
  /** Shown in a chip under the pin, e.g. the node tag or "Node 3" */
  label: string
}

export interface SiteMapImage {
  dataUrl: string
  width: number
  height: number
}

export interface SiteMapOptions {
  /**
   * Site boundary polygon vertices. Drawn as an overlay and included in the
   * framing extent, so the captured area can be widened beyond the trees
   * (e.g. to show encroachment from a neighbouring property).
   */
  boundary?: Array<{ latitude: number; longitude: number }> | null
  /** Brand layout density (009): sizes badges so their text prints at 7 pt */
  density?: Density
  /**
   * Planned badges (009, printed pt on this map). Displaced ones are drawn
   * on a leader line from their tree; absent ⇒ every badge on its tree.
   */
  badges?: Array<{ label: string; anchor: PointPt; centre: PointPt; displaced: boolean }>
}

/** Backing-store multiplier: markers, labels and credits stay crisp in print */
const PIXEL_RATIO = 2

/**
 * Renders the site map. Resolves null when no marker has coordinates or when
 * no tile could be fetched from any source (e.g. offline).
 */
export async function generateSiteMapImage(
  markers: SiteMapMarker[],
  options?: SiteMapOptions
): Promise<SiteMapImage | null> {
  const valid = markers.filter((m) => Number.isFinite(m.latitude) && Number.isFinite(m.longitude))
  const boundary = (options?.boundary ?? []).filter(
    (p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude)
  )
  if (valid.length === 0 && boundary.length < 3) return null

  // Frame on markers AND boundary, so widening the boundary widens the capture
  const framingPoints = [...valid, ...boundary]

  // Region-resolve the imagery from the framing centre (geographic mean)
  const centerLat =
    (Math.min(...framingPoints.map((p) => p.latitude)) +
      Math.max(...framingPoints.map((p) => p.latitude))) /
    2
  const centerLng =
    (Math.min(...framingPoints.map((p) => p.longitude)) +
      Math.max(...framingPoints.map((p) => p.longitude))) /
    2
  const resolved = resolveImagerySource(centerLat, centerLng)
  const sources: ImagerySource[] = resolved.underlay
    ? [resolved.primary, resolved.underlay]
    : [resolved.primary]

  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_WIDTH * PIXEL_RATIO
  canvas.height = CANVAS_HEIGHT * PIXEL_RATIO
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.scale(PIXEL_RATIO, PIXEL_RATIO)

  // Frame at the regional source's full depth; cells it can't supply fall
  // through to the underlay tile by tile.
  const zoom = fitZoom(framingPoints, resolved.primary.maxNativeZoom)
  const xs = framingPoints.map((p) => lonToTileX(p.longitude, zoom) * TILE_SIZE)
  const ys = framingPoints.map((p) => latToTileY(p.latitude, zoom) * TILE_SIZE)
  const originX = (Math.min(...xs) + Math.max(...xs)) / 2 - CANVAS_WIDTH / 2
  const originY = (Math.min(...ys) + Math.max(...ys)) / 2 - CANVAS_HEIGHT / 2
  const toCanvas = (p: { latitude: number; longitude: number }) => ({
    x: lonToTileX(p.longitude, zoom) * TILE_SIZE - originX,
    y: latToTileY(p.latitude, zoom) * TILE_SIZE - originY,
  })

  const usedSources = await drawTiles(ctx, createTileSession(), sources, {
    zoom,
    originX,
    originY,
    width: CANVAS_WIDTH,
    height: CANVAS_HEIGHT,
  })
  if (usedSources.length === 0) return null

  // Site boundary beneath the markers
  if (boundary.length >= 3) drawBoundary(ctx, boundary.map(toCanvas))

  // Markers on top, top-to-bottom so lower badges overlap upper ones. Sized
  // in printed points (009 R1): one legibility standard for every site map
  const ptPerPx = singleMapPtPerPx(options?.density ?? 'standard')
  const badge = {
    fontPx: Math.round(BADGE_TEXT_PT / ptPerPx),
    heightPx: BADGE_HEIGHT_PT / ptPerPx,
    padPx: BADGE_PAD_PT / ptPerPx,
  }
  if (options?.badges) {
    const px = (p: PointPt) => ({ x: p.x / ptPerPx, y: p.y / ptPerPx })
    for (const b of options.badges.filter((x) => x.displaced)) {
      const from = px(b.anchor)
      const to = px(b.centre)
      const long = Math.hypot(b.centre.x - b.anchor.x, b.centre.y - b.anchor.y)
      drawLeaderLine(ctx, from, to, 1 / ptPerPx, long > LONG_LEADER_BADGES * BADGE_HEIGHT_PT)
    }
    // Top-to-bottom so lower badges overlap upper ones
    for (const b of [...options.badges].sort((p, q) => p.centre.y - q.centre.y)) {
      const { x, y } = px(b.centre)
      drawMarker(ctx, x, y, b.label, badge)
    }
  } else {
    const sorted = [...valid].sort((a, b) => b.latitude - a.latitude)
    for (const marker of sorted) {
      const { x, y } = toCanvas(marker)
      drawMarker(ctx, x, y, marker.label, badge)
    }
  }

  drawAttribution(
    ctx,
    usedSources.map((s) => s.attributionPlain).join(' · '),
    CANVAS_WIDTH,
    CANVAS_HEIGHT
  )

  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.85),
    width: canvas.width,
    height: canvas.height,
  }
}

// ---------------------------------------------------------------------------
// 009: single map or map book
// ---------------------------------------------------------------------------

export interface SiteMapRequest {
  trees: SiteTree[]
  boundary: LatLng[] | null
  layout: SiteMapLayout
  density: Density
}

const OVERVIEW_JPEG_QUALITY = 0.85
const SHEET_JPEG_QUALITY = 0.8
/** A leader this many badge heights long prints heavier, to stay traceable (R5) */
const LONG_LEADER_BADGES = 4

/**
 * Plans the site map (planSiteMap) and renders it: today's single map, or an
 * overview plus detail sheets (contracts/site-map-renderer.md). A single map
 * keeps generateSiteMapImage's null-when-no-imagery contract; a map book is
 * always returned, on a neutral background where imagery is missing, because
 * its numbered badges carry the tree identities the report refers to.
 */
export async function generateSiteMap(req: SiteMapRequest): Promise<SiteMapRender | null> {
  const plan = planSiteMap(req)
  if (plan.mode === 'none') return null
  if (plan.mode === 'single') {
    const labels = new Map(req.trees.map((t) => [t.nodeNumber, t.label]))
    const image = await generateSiteMapImage(
      req.trees.map((t) => ({ latitude: t.latitude, longitude: t.longitude, label: t.label })),
      {
        boundary: req.boundary,
        density: req.density,
        badges: plan.placements.map((pl) => ({
          label: labels.get(pl.nodeNumber) ?? String(pl.nodeNumber),
          anchor: pl.anchor,
          centre: { x: pl.badge.x + pl.badge.w / 2, y: pl.badge.y + pl.badge.h / 2 },
          displaced: pl.displaced,
        })),
      }
    )
    return image ? { kind: 'single', imageUrl: image.dataUrl } : null
  }

  const located = plan.sheets.flatMap((s) => s.trees)
  const boundary = (req.boundary ?? []).filter(
    (p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude)
  )
  const lats = located.map((t) => t.latitude)
  const lngs = located.map((t) => t.longitude)
  const resolved = resolveImagerySource(
    (Math.min(...lats) + Math.max(...lats)) / 2,
    (Math.min(...lngs) + Math.max(...lngs)) / 2
  )
  const sources: ImagerySource[] = resolved.underlay
    ? [resolved.primary, resolved.underlay]
    : [resolved.primary]
  const session = createTileSession()

  // Budget the imagery up front (R6): sheets in number order, overview last
  const sheetFrames = plan.sheets.map((sheet) => {
    const width = sheet.frame.widthPt * PX_PER_PT
    const height = sheet.frame.heightPt * PX_PER_PT
    const centre = worldPx(sheet.center, sheet.zoom)
    return {
      zoom: sheet.zoom,
      originX: centre.x - width / 2,
      originY: centre.y - height / 2,
      width,
      height,
    }
  })
  const tileZooms = planTileZooms(
    [...sheetFrames, plan.overview],
    undefined,
    resolved.primary.minZoom
  )
  plan.sheets.forEach((sheet, i) => {
    sheet.tileZoom = tileZooms[i] ?? sheet.tileZoom
  })
  const overviewTileZoom = tileZooms[plan.sheets.length] ?? Math.floor(plan.overview.zoom)

  const overviewUrl = await renderOverview(
    { ...plan.overview, tileZoom: overviewTileZoom },
    located,
    boundary,
    sources,
    session,
    singleMapPtPerPx(req.density)
  )

  // One sheet at a time: each canvas is encoded and released before the next
  // is created, keeping peak memory flat on iOS (R8)
  const sheets = []
  for (const sheet of plan.sheets) {
    sheets.push({
      number: sheet.number,
      scaleLabel: `1:${sheet.scale}`,
      treeCount: sheet.trees.length,
      imageUrl: await renderSheet(
        sheet,
        located,
        boundary,
        sources,
        session,
        plan.overview.sheetOutlines
      ),
      crowded: sheet.crowded,
    })
  }

  return {
    kind: 'book',
    overviewUrl,
    sheets,
    sheetByNode: Object.fromEntries(plan.sheetByNode),
  }
}

function newCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  return { canvas, ctx }
}

/** Overview: imagery, boundary, every tree as a dot, each sheet's outline */
async function renderOverview(
  frame: OverviewFrame & { tileZoom: number },
  trees: SiteTree[],
  boundary: LatLng[],
  sources: ImagerySource[],
  session: TileSession,
  ptPerPx: number
): Promise<string> {
  const { canvas, ctx } = newCanvas(frame.width * PIXEL_RATIO, frame.height * PIXEL_RATIO)
  ctx.scale(PIXEL_RATIO, PIXEL_RATIO)
  const toCanvas = (p: LatLng) => {
    const w = worldPx(p, frame.zoom)
    return { x: w.x - frame.originX, y: w.y - frame.originY }
  }
  const used = await drawTiles(ctx, session, sources, frame)
  if (boundary.length >= 3) drawBoundary(ctx, boundary.map(toCanvas))
  const unitsPerPt = 1 / ptPerPx
  for (const t of trees) {
    const { x, y } = toCanvas(t)
    drawTreeDot(ctx, x, y, unitsPerPt)
  }
  for (const o of frame.sheetOutlines) drawSheetOutline(ctx, o.rect, o.label, unitsPerPt)
  drawAttribution(ctx, attributionText(used), frame.width, frame.height, 1, used.length === 0)
  const url = canvas.toDataURL('image/jpeg', OVERVIEW_JPEG_QUALITY)
  canvas.width = 0
  return url
}

/** Detail sheet, drawn in printed points on a PX_PER_PT backing store */
async function renderSheet(
  sheet: SheetPlan,
  allTrees: SiteTree[],
  boundary: LatLng[],
  sources: ImagerySource[],
  session: TileSession,
  outlines: OverviewFrame['sheetOutlines']
): Promise<string> {
  const { widthPt, heightPt } = sheet.frame
  const widthPx = widthPt * PX_PER_PT
  const heightPx = heightPt * PX_PER_PT
  const { canvas, ctx } = newCanvas(widthPx, heightPx)

  // Imagery in canvas px, where world px at the sheet zoom map 1:1
  const centre = worldPx(sheet.center, sheet.zoom)
  const originX = centre.x - widthPx / 2
  const originY = centre.y - heightPx / 2
  const used = await drawTiles(ctx, session, sources, {
    zoom: sheet.zoom,
    tileZoom: sheet.tileZoom,
    originX,
    originY,
    width: widthPx,
    height: heightPx,
  })

  // Everything else in printed points
  ctx.scale(PX_PER_PT, PX_PER_PT)
  const toFrame = (p: LatLng) => {
    const w = worldPx(p, sheet.zoom)
    return { x: (w.x - originX) / PX_PER_PT, y: (w.y - originY) / PX_PER_PT }
  }
  // Same stroke as the single map's boundary as printed (3.5 px at ≈ 0.36 pt/px)
  if (boundary.length >= 3) drawBoundary(ctx, boundary.map(toFrame), 0.36)

  const own = new Set(sheet.trees.map((t) => t.nodeNumber))
  for (const t of allTrees) {
    if (own.has(t.nodeNumber)) continue
    const p = toFrame(t)
    if (p.x >= 0 && p.y >= 0 && p.x <= widthPt && p.y <= heightPt) drawMutedDot(ctx, p.x, p.y, 1)
  }

  for (const pl of sheet.placements.filter((x) => x.displaced)) {
    const to = { x: pl.badge.x + pl.badge.w / 2, y: pl.badge.y + pl.badge.h / 2 }
    const length = Math.hypot(to.x - pl.anchor.x, to.y - pl.anchor.y)
    drawLeaderLine(ctx, pl.anchor, to, 1, length > LONG_LEADER_BADGES * BADGE_HEIGHT_PT)
  }
  const labels = new Map(sheet.trees.map((t) => [t.nodeNumber, t.label]))
  for (const pl of sheet.placements) {
    drawBadgeAt(ctx, pl.badge, labels.get(pl.nodeNumber) ?? String(pl.nodeNumber), 1)
  }

  // Furniture in the corners the planner kept clear of every tree
  const { metres, barPt } = scaleBarBoxPt(sheet.frame, sheet.scale)
  drawScaleBar(ctx, sheet.furniture.scaleBar, metres, barPt)
  drawLocatorInset(ctx, sheet.furniture.locator, outlines, sheet.number)
  drawAttributionAt(ctx, attributionText(used), sheet.furniture.attribution, used.length === 0)
  const url = canvas.toDataURL('image/jpeg', SHEET_JPEG_QUALITY)
  canvas.width = 0
  return url
}

const attributionText = (used: ImagerySource[]) => used.map((s) => s.attributionPlain).join(' · ')
